// THE RAIL — one shape, one tone vocabulary, one detail budget, one composer.
//
// Shared by the server and both SPAs so the two sides can never disagree (the convention
// src/shared/contact.ts established). NOTHING here may import `pg`, React, or anything from
// src/server — this module is bundled into the browser by esbuild.config.js.
//
// Why this file exists (FEASIBILITY BL-5/BL-6): four studies re-declared `Field` from
// briefQueries.ts, which is unusable client-side (it imports `pool`), whose `key` is a closed
// union of five client-page keys no other rail can satisfy, and whose `tone` union lacks the
// states the ATTEST vocabulary needs. And `cutRun` — cited as "the exported composer at
// briefQueries.ts:554-562" — was an anonymous IIFE closed over local state, exported by nothing.
//
// The rail is a fixed set of cells in a fixed order. Every cell renders in every state; a cell
// that cannot be computed renders '—' with a tone that SAYS SO. That is B8, in one place:
// a missing check never renders as a passed check.

/**
 * Routes are CONSTANT per cell — they never vary with the value (only anchors carry refs).
 * The server emits `RouteRef` shapes ({kind:'screen'|'in-page'|'related', …}); the client
 * resolves them through `openRoute`. Kept open so a rail can carry any of them.
 */
export type RailRoute = Record<string, unknown> | null;

/**
 * The ATTEST tone vocabulary — the full set, product-wide.
 *
 *   ink      a plain figure, nothing to say about it
 *   warn     the figure is the operator's problem
 *   ok       the figure is a settled good state
 *   err      the figure states a failure that happened
 *   nil      there is nothing to count and the schema says so      (`.n-zero` / `.n-none`)
 *   broken   the producer of this figure is down                   (dashboard §1 CLOCK)
 *   attend   not broken, not wrong — it wants a look               (dashboard §1 CLOCK)
 *   unknown  the check did not run; this is NOT a pass             (`.n-unknown`, B8)
 *
 * `nil` and `unknown` are different values and must render differently: `nil` means "counted,
 * and the count is zero"; `unknown` means "not counted".
 */
export type RailTone = 'ink' | 'warn' | 'ok' | 'err' | 'nil' | 'broken' | 'attend' | 'unknown';

export const RAIL_TONES: RailTone[] = ['ink', 'warn', 'ok', 'err', 'nil', 'broken', 'attend', 'unknown'];

export const isRailTone = (v: unknown): v is RailTone =>
  typeof v === 'string' && (RAIL_TONES as string[]).includes(v);

/**
 * One rail cell.
 *
 * `key` is a plain string here and narrowed per rail by the consumer — every rail has its own
 * closed union (`SCOPE/HELD/FUNDED/PROVEN/SOURCED`, `CORPUS/DATED/READ/PAIRS/YIELD`, …) and no
 * single union can serve eleven of them.
 *
 * `value` is ALREADY FORMATTED — '—' when unknown, never null, never empty.
 * `detail` is the provenance line — never empty; it says where the figure came from, or why
 * there isn't one. A cell whose detail is blank is a defect.
 */
export interface RailCell {
  key: string;
  value: string;
  unit: string | null;
  detail: string;
  tone: RailTone;
  route: RailRoute;
}

/**
 * §v3.1.R: a rail detail is a function of the DATA alone, never the viewport. 27 characters is
 * the narrowest cell in the product under UNIFIED-DESIGN §6.3 — the rail geometry is identical
 * on every tab, so there is exactly one budget, not one per study. (Portal-admin's 34 and
 * proposals' 38 are superseded; UNIFIED-FUNCTION §0.3.)
 */
export const DETAIL_RUN_BUDGET = 27;

/**
 * Compose a run of parts into one detail line inside `budget` characters.
 *
 * Drops parts from the END and states the remainder as ' +N' — so the first part (the one the
 * caller ordered first, i.e. the one that matters) always survives. At least one part is always
 * kept, even when it alone exceeds the budget: an over-long truthful part beats an ellipsis.
 *
 *   cutRun(['ZZBRFB01 · design', 'ZZBRFA01 · discovery'])  →  'ZZBRFB01 · design +1'
 *   cutRun([])                                             →  ''
 *
 * The empty-run SENTENCE is the caller's, not this function's: "no engagement" and
 * "3 archived engagements" are client-page copy, and every rail words its absence differently.
 */
export function cutRun(parts: string[], budget: number = DETAIL_RUN_BUDGET): string {
  if (!parts.length) return '';
  const compose = (k: number) =>
    parts.slice(0, k).join(', ') + (k < parts.length ? ` +${parts.length - k}` : '');
  let kept = parts.length;
  while (kept > 1 && compose(kept).length > budget) kept -= 1;
  return compose(kept);
}

/**
 * The degraded rail (F4, F5.1): all cells render '—' with a STATED reason, geometry preserved,
 * no stale number left standing. `keys` fixes the set and the order; the reason is rendered as
 * every cell's detail, so the operator reads why on whichever cell they happen to look at.
 */
export function degradedRail(keys: readonly string[], reason: string): RailCell[] {
  return keys.map((key) => ({
    key,
    value: '—',
    unit: null,
    detail: reason,
    tone: 'unknown' as const,
    route: null,
  }));
}
